import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Check, PowerOff } from "lucide-react";

type Theme = {
  key: string;
  label: string;
  description: string | null;
  badge_emoji: string | null;
  badge_text: string | null;
  accent_hsl: string;
  ribbon_gradient: string;
  footer_note: string | null;
  active: boolean;
};

const SeasonalThemeTab = () => {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("seasonal_themes")
      .select("*")
      .order("label");
    if (error) {
      toast.error(error.message);
      return;
    }
    setThemes((data || []) as Theme[]);
  };

  useEffect(() => {
    load();
  }, []);

  const activate = async (key: string) => {
    setSaving(key);
    try {
      // Clear all first (single active row constraint), then set this one.
      const { error: clearErr } = await supabase
        .from("seasonal_themes")
        .update({ active: false })
        .eq("active", true);
      if (clearErr) throw clearErr;
      const { error: setErr } = await supabase
        .from("seasonal_themes")
        .update({ active: true })
        .eq("key", key);
      if (setErr) throw setErr;
      toast.success("Seasonal theme updated");
      await load();
    } catch (e: any) {
      toast.error(e.message || "Failed to update theme");
    } finally {
      setSaving(null);
    }
  };

  const disableAll = async () => {
    setSaving("__off__");
    try {
      const { error } = await supabase
        .from("seasonal_themes")
        .update({ active: false })
        .eq("active", true);
      if (error) throw error;
      toast.success("Seasonal theme disabled");
      await load();
    } catch (e: any) {
      toast.error(e.message || "Failed to disable theme");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-xl font-semibold">Seasonal Theme</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Subtle accents only — header ribbon, hero badge, and footer note. The
            sage/dark brand stays intact.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={disableAll} disabled={!!saving}>
          <PowerOff className="h-4 w-4 mr-2" />
          Disable seasonal theme
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {themes.map((t) => (
          <div
            key={t.key}
            className={`bg-card border rounded-xl p-5 ${
              t.active ? "border-primary" : "border-border"
            }`}
          >
            <div
              className="h-2 w-full rounded-full mb-4"
              style={{ background: t.ribbon_gradient }}
            />
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h3 className="font-display font-bold text-base">
                  {t.badge_emoji} {t.label}
                </h3>
                {t.description && (
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {t.description}
                  </p>
                )}
              </div>
              <span
                className="text-[10px] font-mono px-2 py-1 rounded"
                style={{
                  background: `hsl(${t.accent_hsl} / 0.12)`,
                  color: `hsl(${t.accent_hsl})`,
                }}
              >
                hsl({t.accent_hsl})
              </span>
            </div>

            {t.badge_text && (
              <p className="text-xs text-muted-foreground mt-2">
                <span className="font-semibold text-foreground">Badge:</span>{" "}
                {t.badge_text}
              </p>
            )}
            {t.footer_note && (
              <p className="text-xs text-muted-foreground mt-1">
                <span className="font-semibold text-foreground">Footer:</span>{" "}
                {t.footer_note}
              </p>
            )}

            <div className="mt-4">
              {t.active ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                  <Check className="h-3.5 w-3.5" /> Active
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!!saving}
                  onClick={() => activate(t.key)}
                >
                  {saving === t.key ? "Setting…" : "Set active"}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SeasonalThemeTab;